import { Alert } from '@platform/ui';
import { Link } from '@tanstack/react-router';

import { useMessages } from '../i18n';
import type { LimitState } from '../lib/api';
import { useSubscription } from '../lib/billing';
import { useFormat } from '../lib/format';
import { useCanOpen } from '../lib/session';

/** The limits a seller adds against; the month's orders are counted, never refused (ADR 0017 §1). */
export type AddedResource = 'products' | 'channels' | 'users';

/**
 * The sentence for a limit the account has reached or is above: reached says
 * what keeps working and how to add again, above says that what exists is
 * kept (docs/15 §2). Nothing for a limit with room, or one the package leaves
 * empty.
 */
export function useLimitSentence() {
    const t = useMessages();
    const format = useFormat();
    return (state: LimitState): string | undefined => {
        if (state.resource === 'orders_monthly' || state.used < state.limit) return undefined;
        return state.used > state.limit
            ? t.planLimits.over[state.resource](format.count(state.used), format.count(state.limit))
            : t.planLimits.reached[state.resource];
    };
}

/**
 * Beside the list a seller adds to — Ürünler, Satış Kanalları, Ekip — once its
 * limit is reached, before an add is tried and refused, with the way to the
 * packages. Asked only of someone who may read the subscription; anyone else
 * meets the same words in the refusal itself.
 */
export function PlanLimitAlert({ resource }: { resource: AddedResource }) {
    const t = useMessages();
    const canOpen = useCanOpen();
    const sentence = useLimitSentence();
    const subscription = useSubscription(canOpen('/abonelik'));
    const state = subscription.data?.limitStates.find((limit) => limit.resource === resource);
    const words = state && sentence(state);
    if (!words) return null;
    return (
        <Alert tone="warning">
            {words}{' '}
            <Link to="/abonelik" className="font-medium underline underline-offset-2">
                {t.subscription.viewPlans}
            </Link>
        </Alert>
    );
}
